import { useEffect, useState } from 'react';
import Select from 'react-select';
import { getAllServices } from '../../../../../sources/service';
import translations from '../../../../../translations';
import type { ServiceElementResponseAPI } from '../../../../../types/service';
import { isNullOrEmpty } from '../../../../../utils/guard';
import { getSharedStyles } from '../../../../../utils/select';
import ButtonDefault from '../../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../../buttons/ButtonPrimary';
import FormGroup from '../../../../generic/FormGroup';
import ModalBody from '../../../../generic/modal/ModalBody';
import ModalFooter from '../../../../generic/modal/ModalFooter';
import Loader from '../../../../loader/Loader';
import { useNotificationDispatch } from '../../../../notifications/NotificationProvider';
import ValueSelectorModal from '../../../../values/selector/ValueSelectorModal';
import { useListener } from '../../contextProviders/ListenerProvider';
import { useMapElement } from '../../contextProviders/MapElementProvider';

const ListenerDetails = () => {
    const [services, setServices] = useState<ServiceElementResponseAPI[]>([]);
    const [isLoadingServices, setIsLoadingServices] = useState(true);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const { mapElement, setConfigTitle } = useMapElement();
    const dispatchNotification = useNotificationDispatch();

    const {
        listenerToEdit,
        onSelectDeveloperName,
        onSelectListenerType,
        onSelectServiceElementId,
        onSelectValueReference,
        onApplyListener,
        onReturnToDefaultScreen,
    } = useListener();

    const { container } = useMapElement();

    const { listener, index } = listenerToEdit;

    const isNameValid = !isNullOrEmpty(listener?.developerName);
    const isTypeValid = !isNullOrEmpty(listener?.listenerType);
    const isSelectedServiceElementValid = !isNullOrEmpty(listener?.serviceElementId);
    const isSelectedValueValid = !isNullOrEmpty(listener?.valueElementToReferenceForListeningId);
    const isFormValid =
        isNameValid && isTypeValid && isSelectedServiceElementValid && isSelectedValueValid;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        (async () => {
            try {
                const response = await getAllServices();

                setServices(response);
                setIsLoadingServices(false);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            }
        })();
    }, []);

    useEffect(() => {
        setConfigTitle(`Listener for: ${mapElement.developerName}`);

        return () => setConfigTitle(null);
    }, [mapElement.developerName, setConfigTitle]);

    const onSave = () => {
        setHasSubmitted(true);

        if (isFormValid && index !== null) {
            onApplyListener(index);
        }
    };

    const renderBody = () => (
        <>
            <FormGroup
                label="Name"
                htmlFor="listenerName"
                isRequired
                isValid={isNameValid}
                validationMessage="Please enter a name."
                showValidation={hasSubmitted}
            >
                <input
                    id="listenerName"
                    value={listener?.developerName ?? ''}
                    onChange={({ target: { value } }) => onSelectDeveloperName(value)}
                    size={25}
                    required
                    className="form-control"
                    type="text"
                />
            </FormGroup>

            <FormGroup
                label="Listen to events of type"
                htmlFor="listenerType"
                isRequired
                isValid={isTypeValid}
                validationMessage="Please enter a type."
                showValidation={hasSubmitted}
            >
                <input
                    id="listenerType"
                    value={listener?.listenerType ?? ''}
                    onChange={({ target: { value } }) => onSelectListenerType(value)}
                    size={25}
                    required
                    className="form-control"
                    type="text"
                />
            </FormGroup>

            {isLoadingServices ? (
                <Loader />
            ) : (
                <FormGroup
                    label="The connector implementing this listener"
                    htmlFor="service-select"
                    isRequired
                    isValid={isSelectedServiceElementValid}
                    validationMessage="Please select a connector."
                    showValidation={hasSubmitted}
                >
                    <Select
                        inputId="service-select"
                        className="form-control-width"
                        onChange={(selection) =>
                            selection && onSelectServiceElementId(selection.value)
                        }
                        value={{
                            label: services.find((m) => m.id === listener?.serviceElementId)
                                ?.developerName,
                            value: listener?.serviceElementId || '',
                        }}
                        options={services.map((s) => ({
                            value: s.id,
                            label: s.developerName,
                        }))}
                        styles={getSharedStyles<{
                            value: string;
                            label: string | null | undefined;
                        }>()}
                    />
                </FormGroup>
            )}

            <FormGroup
                label="The Value being listened to"
                isRequired
                isValid={isSelectedValueValid}
                validationMessage="Please select a value."
                showValidation={hasSubmitted}
            >
                <ValueSelectorModal
                    value={listener?.valueElementToReferenceForListeningId ?? null}
                    onChange={onSelectValueReference}
                    container={container}
                    includeSystemValues={false}
                />
            </FormGroup>
        </>
    );

    const renderFooter = () => (
        <>
            <ButtonDefault className="flex-child-right" onClick={onReturnToDefaultScreen}>
                Cancel
            </ButtonDefault>
            <ButtonPrimary className="margin-left" onClick={onSave}>
                {translations.GRAPH_config_panel_add}
            </ButtonPrimary>
        </>
    );

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>{renderFooter()}</ModalFooter>
        </>
    );
};

export default ListenerDetails;
